import React, { useState, useEffect, useMemo } from 'react';
import DocumentsList from '../DocumentsList/DocumentsList';
import './Coordonnees.css';

interface Titulaire {
    rang: number;
    region: boolean;
    contrat: {
        id: number;
        numero: string;
    };
    entite: {
        id: number;
        nom: string;
        siret: string;
        adresse: {
            numero_voie: string;
            type_voie: string;
            nom_voie: string;
            code_postal: string;
            ville: string;
            arrondissement: string;
        };
    };
    interlocuteur: {
        id: number;
        nom: string;
        prenom: string;
        email_contact: string;
        telephone: string;
        fonction: string;
    } | null;
}

interface CoordonneesProps {
    prestataire?: any;
    commande?: any;
    operation?: any;
    isAcheteur: boolean;
    isPresta: boolean;
    showSignataire?: boolean;
    showDocuments?: boolean;
    showTitulaires?: boolean;
    lotDepartementId?: number;
    lotTechniqueId?: number;
}

const Coordonnees: React.FC<CoordonneesProps> = ({
    prestataire,
    commande,
    operation,
    isAcheteur,
    isPresta,
    showSignataire,
    showDocuments = true,
    showTitulaires = false,
    lotDepartementId,
    lotTechniqueId
}) => {
    const [documents, setDocuments] = useState<any[]>([]);
    const [documentsCommuns, setDocumentsCommuns] = useState<any[]>([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [titulaires, setTitulaires] = useState<Titulaire[]>([]);
    const [loadingTitulaires, setLoadingTitulaires] = useState(false);

    const contratId = prestataire?.contrat?.id || commande?.contrat?.id;
    // ID de l'entité entreprise pour les documents entreprise
    const entiteEntrepriseId = prestataire?.contrat?.entite?.id || commande?.contrat?.entite?.id;

    // Récupérer les dates de signature depuis l'historique
    // L'historique peut être dans commande.historique (pour le prestataire) ou operation.historique (pour l'acheteur)
    const historique = commande?.historique || operation?.historique || prestataire?.historique || [];

    const signatureAcheteurDate = useMemo(() => {
        const entry = historique.find((h: any) => h.prefix === 'Signature Publique');
        if (entry?.created_at) {
            return new Date(entry.created_at).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        return null;
    }, [historique]);

    const signaturePrestataireDate = useMemo(() => {
        const entry = historique.find((h: any) => h.prefix === 'Signature Privée');
        if (entry?.created_at) {
            return new Date(entry.created_at).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        return null;
    }, [historique]);

    useEffect(() => {
        if (!showDocuments) return;
        if (!contratId && !entiteEntrepriseId) return;

        let isCancelled = false;

        const fetchDocuments = async () => {
            setLoadingDocs(true);
            try {
                let allDocs: any[] = [];

                // 1. Récupérer les documents entreprise (si ID entité disponible)
                if (entiteEntrepriseId) {
                    try {
                        const resEntreprise = await fetch(`${process.env.REACT_APP_API_URL}/documents/entreprise/${entiteEntrepriseId}`, {
                            method: 'GET',
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'application/json',
                            },
                        });

                        if (!isCancelled && resEntreprise.ok) {
                            const jsonEntreprise = await resEntreprise.json();
                            const docsEntreprise = (jsonEntreprise.data || []).map((doc: any) => ({
                                ...doc,
                                category: 'entreprise'
                            }));
                            allDocs = [...allDocs, ...docsEntreprise];
                        }
                    } catch (err) {
                        if (!isCancelled) {
                            console.error('Erreur lors de la récupération des documents entreprise:', err);
                        }
                    }
                }

                // 2. Récupérer les documents accord-cadre (contrat PDF) si ID contrat disponible
                if (contratId) {
                    try {
                        const resAccordCadre = await fetch(`${process.env.REACT_APP_API_URL}/documents/accord-cadre/${contratId}`, {
                            method: 'GET',
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'application/json',
                            },
                        });

                        if (!isCancelled && resAccordCadre.ok) {
                            const jsonAccordCadre = await resAccordCadre.json();
                            const docsAccordCadre = (jsonAccordCadre.data || []).map((doc: any) => ({
                                ...doc,
                                category: 'accord-cadre'
                            }));
                            allDocs = [...allDocs, ...docsAccordCadre];
                        }
                    } catch (err) {
                        if (!isCancelled) {
                            console.error('Erreur lors de la récupération des documents accord-cadre:', err);
                        }
                    }
                }

                if (!isCancelled) {
                    // 🔹 Filtrer les documents à ne pas afficher
                    const filteredDocs = allDocs.filter(
                        (doc: any) =>
                            !doc.type?.startsWith('MS') && !doc.type?.startsWith('25BC')
                    );

                    setDocuments(filteredDocs);
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error(err);
                    setDocuments([]);
                }
            } finally {
                if (!isCancelled) {
                    setLoadingDocs(false);
                }
            }
        };

        fetchDocuments();

        return () => {
            isCancelled = true;
        };
    }, [contratId, entiteEntrepriseId, showDocuments]);

    // Effet pour charger les documents communs
    useEffect(() => {
        if (!showDocuments) return;

        let isCancelled = false;

        const fetchDocumentsCommuns = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/documents-communs?per_page=100`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!isCancelled && res.ok) {
                    const json = await res.json();
                    const docsCommuns = (json.data?.data || [])
                        .filter((doc: any) => doc.actif)
                        .map((doc: any) => ({
                            id: doc.id,
                            type: doc.type,
                            nom: doc.nom,
                            description: doc.description,
                            extension: doc.extension,
                            taille: doc.taille,
                            updated_at: doc.updated_at,
                            category: 'commun'
                        }));
                    setDocumentsCommuns(docsCommuns);
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error('Erreur lors de la récupération des documents communs:', err);
                    setDocumentsCommuns([]);
                }
            }
        };

        fetchDocumentsCommuns();

        return () => {
            isCancelled = true;
        };
    }, [showDocuments]);

    // Effet pour charger les titulaires du lot
    useEffect(() => {
        if (!showTitulaires || !lotDepartementId) return;

        const fetchTitulaires = async () => {
            setLoadingTitulaires(true);
            try {
                // Construire l'URL avec le paramètre optionnel lot_technique_id
                let url = `${process.env.REACT_APP_API_URL}/lots-departements/${lotDepartementId}/titulaires`;
                if (lotTechniqueId) {
                    url += `?lot_technique_id=${lotTechniqueId}`;
                }

                const res = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!res.ok) throw new Error('Erreur lors de la récupération des titulaires');

                const json = await res.json();
                setTitulaires(json.data?.titulaires || []);
            } catch (err) {
                console.error(err);
                setTitulaires([]);
            } finally {
                setLoadingTitulaires(false);
            }
        };

        fetchTitulaires();
    }, [showTitulaires, lotDepartementId, lotTechniqueId]);

    const prestaAdress = prestataire?.contrat?.entite?.adresse
        ? `${prestataire.contrat.entite.adresse.numero_voie || ''} ${prestataire.contrat.entite.adresse.type_voie || ''} ${prestataire.contrat.entite.adresse.nom_voie || ''}, ${prestataire.contrat.entite.adresse.code_postal || ''} ${prestataire.contrat.entite.adresse.ville || ''} ${prestataire.contrat.entite.adresse.arrondissement || ''}`
        : `${commande?.contrat?.entite?.adresse?.numero_voie || ''} ${commande?.contrat?.entite?.adresse?.type_voie || ''} ${commande?.contrat?.entite?.adresse?.nom_voie || ''}, ${commande?.contrat?.entite?.adresse?.code_postal || ''} ${commande?.contrat?.entite?.adresse?.ville || ''} ${commande?.contrat?.entite?.adresse?.arrondissement || ''}`;

    // Helper pour formater une adresse
    const formatAddress = (adresse: any) => {
        if (!adresse) return '';
        const parts = [
            adresse.numero_voie,
            adresse.type_voie,
            adresse.nom_voie
        ].filter(p => p && p.trim()).join(' ');

        // Formater la ville : première lettre en majuscule, reste inchangé
        const formatCity = (city: string | null | undefined) => {
            if (!city) return '';
            const trimmed = city.trim();
            return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLocaleLowerCase();
        };

        const cityParts = [
            adresse.code_postal,
            formatCity(adresse.ville),
            adresse.arrondissement
        ].filter(p => p && p.trim()).join(' ');

        return [parts, cityParts].filter(p => p).join(', ');
    };

    // Helper pour normaliser le téléphone
    const formatPhone = (phone: string | null | undefined) => {
        if (!phone) return '';
        // Enlever tous les caractères non numériques
        const cleaned = phone.replace(/\D/g, '');
        // Formater en +33 6 XX XX XX XX si 10 chiffres commençant par 0
        if (cleaned.length === 10 && cleaned.startsWith('0')) {
            const withoutZero = cleaned.substring(1);
            return `+33 ${withoutZero.charAt(0)} ${withoutZero.substring(1, 3)} ${withoutZero.substring(3, 5)} ${withoutZero.substring(5, 7)} ${withoutZero.substring(7, 9)}`;
        }
        // Si déjà au format international (9 chiffres après indicatif)
        if (cleaned.length === 11 && cleaned.startsWith('33')) {
            const withoutCountry = cleaned.substring(2);
            return `+33 ${withoutCountry.charAt(0)} ${withoutCountry.substring(1, 3)} ${withoutCountry.substring(3, 5)} ${withoutCountry.substring(5, 7)} ${withoutCountry.substring(7, 9)}`;
        }
        return phone;
    };

    // Helper pour normaliser le nom (NOM en majuscules)
    const formatLastName = (name: string | null | undefined) => {
        if (!name) return '';
        return name.trim().toUpperCase();
    };

    // Helper pour normaliser le prénom (Première lettre en majuscule)
    const formatFirstName = (name: string | null | undefined) => {
        if (!name) return '';
        const trimmed = name.trim();
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    };

    // Helper pour normaliser la fonction/titre
    const formatFunction = (func: string | null | undefined) => {
        if (!func) return '';
        return func.trim();
    };

    // Helper pour normaliser l'email (en minuscules)
    const formatEmail = (email: string | null | undefined) => {
        if (!email) return '';
        return email.trim().toLowerCase();
    };

    // Mode titulaires : afficher l'acheteur et tous les titulaires du lot
    if (showTitulaires) {
        return (
            <>
                <div className="coordinates-columns acheteur-only">
                    {/* Colonne Acheteur */}
                    <div className="coordinates-column">
                        <h4>Acheteur : {operation?.organisme?.nom || commande?.operation?.organisme?.nom}</h4>
                        <div className="details-block">
                            <p>{formatAddress(operation?.organisme?.adresse || commande?.operation?.organisme?.adresse)}</p>
                            <p className="section-separator">Contact : {formatFirstName(operation?.organisme?.acheteur?.prenom || commande?.operation?.acheteur?.prenom)} {formatLastName(operation?.organisme?.acheteur?.nom || commande?.operation?.acheteur?.nom)}</p>
                            <p>{formatFunction(operation?.organisme?.acheteur?.fonction || commande?.operation?.acheteur?.fonction)}</p>
                            <p>{formatEmail(operation?.organisme?.acheteur?.email_contact || commande?.operation?.acheteur?.email_contact)}</p>
                            <p>{formatPhone(operation?.organisme?.acheteur?.telephone || commande?.operation?.acheteur?.telephone)}</p>
                        </div>
                    </div>
                </div>

                {/* Section Titulaires du lot */}
                <div className="coordinates-columns titulaires-section">
                    {loadingTitulaires ? (
                        <p>Chargement des titulaires...</p>
                    ) : titulaires.length === 0 ? (
                        <div className="empty-details">
                            <p>Aucun titulaire pour ce lot.</p>
                        </div>
                    ) : (
                        titulaires.map((titulaire, index) => (
                            <div key={index} className="coordinates-column">
                                <h4 style={{ display: "flex", flexDirection: "column" }}>
                                    <span>Prestataire {titulaire.rang} : </span>
                                    <span className='titulaire-name'>{titulaire.entite.nom}</span>
                                </h4>
                                <div className="details-block">
                                    <p>{formatAddress(titulaire.entite.adresse)}</p>
                                    <p>Contrat N°{titulaire.contrat.numero}</p>
                                    {titulaire.interlocuteur && (
                                        <>
                                            <p className="section-separator">Contact : {formatFirstName(titulaire.interlocuteur.prenom)} {formatLastName(titulaire.interlocuteur.nom)}</p>
                                            <p>{formatFunction(titulaire.interlocuteur.fonction)}</p>
                                            <p>{formatEmail(titulaire.interlocuteur.email_contact)}</p>
                                            <p>{formatPhone(titulaire.interlocuteur.telephone)}</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </>
        );
    }

    // Mode normal : afficher les coordonnées prestataire/acheteur + documents
    return (
        <>
            <div className="coordinates-columns">
                {/* Colonne Prestataire */}
                <div className="coordinates-column">
                    <h4>Prestataire : {prestataire?.contrat?.entite?.nom ?? commande?.contrat?.entite?.nom}</h4>
                    {isAcheteur ? (
                        <div className="details-block">
                            <p>{prestaAdress}</p>
                            <p>Contrat N°{prestataire?.contrat?.numero || ""}</p>
                            <p>Lot : {prestataire?.contrat?.lot_technique?.id || ""} - {prestataire?.contrat?.lot_technique?.nom || ""}, {(prestataire?.contrat?.lot_departement?.departement?.id || 38).toString().padStart(2, "0")} {"(" + prestataire?.contrat?.lot_departement?.departement?.nom + ")" || ""}</p>

                            <p className="section-separator">Contact : {formatFirstName(prestataire?.contrat?.entite?.interlocuteur?.prenom ?? prestataire?.prestataire_signataire?.prenom)} {formatLastName(prestataire?.contrat?.entite?.interlocuteur?.nom ?? prestataire?.prestataire_signataire?.nom)}</p>
                            <p>{formatFunction(prestataire?.contrat?.entite?.interlocuteur?.fonction || prestataire?.prestataire_signataire?.fonction)}</p>
                            <p>{formatEmail(prestataire?.contrat?.entite?.interlocuteur?.email_contact || prestataire?.prestataire_signataire?.email_contact)}</p>
                            <p>{formatPhone(prestataire?.contrat?.entite?.interlocuteur?.telephone || prestataire?.prestataire_signataire?.telephone)}</p>

                            {showSignataire && (
                                <>
                                    <p className="section-separator">Signataire : {formatFirstName(prestataire?.prestataire_signataire?.prenom)} {formatLastName(prestataire?.prestataire_signataire?.nom)}</p>
                                    <p>{formatFunction(prestataire?.prestataire_signataire?.fonction)}</p>
                                    <p>{formatEmail(prestataire?.prestataire_signataire?.email_contact)}</p>
                                    <p>{formatPhone(prestataire?.prestataire_signataire?.telephone)}</p>
                                    {signaturePrestataireDate && (
                                        <p className="signature-date">Signé le : {signaturePrestataireDate}</p>
                                    )}
                                </>
                            )}

                        </div>
                    ) : isPresta ? (
                        <div className="details-block">
                            <p>{prestaAdress}</p>
                            <p>Contrat N°{commande?.contrat?.numero || ""}</p>
                            <p>Lot : {commande?.operation?.lot_technique?.id || ""} - {commande?.operation?.lot_technique?.nom || ""}, {(commande?.operation?.lot_departement?.departement?.id || 38).toString().padStart(2, "0")} {"(" + commande?.operation?.lot_departement?.departement?.nom + ")" || ""}</p>

                            <p className="section-separator">Contact : {formatFirstName(commande?.contrat?.entite?.interlocuteur?.prenom)} {formatLastName(commande?.contrat?.entite?.interlocuteur?.nom)}</p>
                            <p>{formatFunction(commande?.contrat?.entite?.interlocuteur?.fonction)}</p>
                            <p>{formatEmail(commande?.contrat?.entite?.interlocuteur?.email_contact)}</p>
                            <p>{formatPhone(commande?.contrat?.entite?.interlocuteur?.telephone)}</p>

                            {showSignataire && (
                                <>
                                    <p className="section-separator">Signataire : {formatFirstName(commande?.prestataire_signataire?.prenom)} {formatLastName(commande?.prestataire_signataire?.nom)}</p>
                                    <p>{formatFunction(commande?.prestataire_signataire?.fonction)}</p>
                                    <p>{formatEmail(commande?.prestataire_signataire?.email_contact)}</p>
                                    <p>{formatPhone(commande?.prestataire_signataire?.telephone)}</p>
                                    {signaturePrestataireDate && (
                                        <p className="signature-date">Signé le : {signaturePrestataireDate}</p>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="empty-details">
                            <p>Aucun prestataire sélectionné pour ce marché.</p>
                        </div>
                    )}
                </div>

                {/* Colonne Acheteur / Organisme */}
                <div className="coordinates-column">
                    <h4>Acheteur : {operation?.organisme?.nom || commande?.operation?.organisme?.nom}</h4>
                    {isAcheteur ? (
                        <div className="details-block">
                            <p>{operation?.organisme?.adresse?.numero_voie} {operation?.organisme?.adresse?.type_voie} {operation?.organisme?.adresse?.nom_voie}, {operation?.organisme?.adresse?.code_postal} {operation?.organisme?.adresse?.ville} {operation?.organisme?.adresse?.arrondissement}</p>

                            <p className="section-separator">Contact : {formatFirstName(operation?.organisme?.acheteur?.prenom)} {formatLastName(operation?.organisme?.acheteur?.nom)}</p>
                            <p>{formatFunction(operation?.organisme?.acheteur?.fonction)}</p>
                            <p>{formatEmail(operation?.organisme?.acheteur?.email_contact)}</p>
                            <p>{formatPhone(operation?.organisme?.acheteur?.telephone)}</p>

                            {showSignataire && (
                                <>
                                    <p className="section-separator">Signataire : {formatFirstName(operation?.acheteur_signataire?.prenom)} {formatLastName(operation?.acheteur_signataire?.nom)}</p>
                                    <p>{formatFunction(operation?.acheteur_signataire?.fonction)}</p>
                                    <p>{formatEmail(operation?.acheteur_signataire?.email_contact || operation?.email)}</p>
                                    <p>{formatPhone(operation?.acheteur_signataire?.telephone || operation?.telephone)}</p>
                                    {signatureAcheteurDate && (
                                        <p className="signature-date">Signé le : {signatureAcheteurDate}</p>
                                    )}
                                </>)}
                            <p className="section-separator">Facturation suivie par : {operation?.organisme?.facturation || ""}</p>
                        </div>
                    ) : isPresta ? (
                        <div className="details-block">
                            <p>{commande?.operation?.organisme?.adresse?.numero_voie} {commande?.operation?.organisme?.adresse?.type_voie} {commande?.operation?.organisme?.adresse?.nom_voie}, {commande?.operation?.organisme?.adresse?.code_postal} {commande?.operation?.organisme?.adresse?.ville} {commande?.operation?.organisme?.adresse?.arrondissement}</p>

                            <p className="section-separator">Contact : {formatFirstName(commande?.operation?.acheteur?.prenom)} {formatLastName(commande?.operation?.acheteur?.nom)}</p>
                            <p>{formatFunction(commande?.operation?.acheteur?.fonction)}</p>
                            <p>{formatEmail(commande?.operation?.acheteur?.email_contact)}</p>
                            <p>{formatPhone(commande?.operation?.acheteur?.telephone)}</p>

                            {showSignataire && (
                                <>
                                    <p className="section-separator">Signataire : {formatFirstName(commande?.operation?.acheteur_signataire?.prenom)} {formatLastName(commande?.operation?.acheteur_signataire?.nom)}</p>
                                    <p>{formatFunction(commande?.operation?.acheteur_signataire?.fonction)}</p>
                                    <p>{formatEmail(commande?.operation?.acheteur_signataire?.email_contact)}</p>
                                    <p>{formatPhone(commande?.operation?.acheteur_signataire?.telephone)}</p>
                                    {signatureAcheteurDate && (
                                        <p className="signature-date">Signé le : {signatureAcheteurDate}</p>
                                    )}
                                </>
                            )}
                            <p className="section-separator">Facturation suivie par : {commande?.operation?.facturation || ""}</p>
                        </div>
                    ) : (
                        <div className="empty-details">
                            <p>Informations client non disponibles.</p>
                        </div>
                    )}
                </div>


            </div>
            {showDocuments && (
                <div className="documents-section">
                    {loadingDocs ? (
                        <p>Chargement des documents...</p>
                    ) : (
                        <DocumentsList
                            documents={[...documents, ...documentsCommuns]}
                            variant='inline'
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default Coordonnees;